import { Link } from "react-router-dom";
import { Minus, Plus, X } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { useBag } from "@/context/BagContext";
import { COLOR_META, SHIPPING, formatPrice } from "@/lib/meta";
import Placeholder from "@/components/Placeholder";

export default function Bag() {
  const { t, lang } = useLang();
  const { items, setQty, remove, subtotal } = useBag();

  if (items.length === 0) {
    return (
      <div className="py-32 text-center px-6" data-testid="bag-empty">
        <p className="eyebrow mb-4">MIORA</p>
        <h1 className="font-serif font-light text-4xl lg:text-5xl text-ink mb-4">{t.bag.title}</h1>
        <p className="text-ink/55 text-sm max-w-sm mx-auto mb-10">{t.bag.emptySub}</p>
        <Link to="/boutique" className="btn btn-ink" data-testid="bag-empty-cta">{t.bag.ctaShop}</Link>
      </div>
    );
  }

  const fee = subtotal >= SHIPPING.FREE_THRESHOLD ? 0 : SHIPPING.STANDARD_FEE;
  const total = subtotal + fee;
  const remaining = SHIPPING.FREE_THRESHOLD - subtotal;

  return (
    <div className="py-12 lg:py-16 pb-24" data-testid="bag-page">
      <div className="max-w-[1200px] mx-auto px-5 lg:px-10">
        <h1 className="font-serif font-light text-4xl lg:text-5xl text-ink mb-12">
          {t.bag.title} <span className="text-ink/35 text-2xl">({items.length})</span>
        </h1>

        <div className="grid lg:grid-cols-[1.6fr_1fr] gap-14 items-start">
          <ul className="divide-y divide-ink/10">
            {items.map((it, i) => (
              <li key={it.key} className="py-7 first:pt-0 flex gap-5 sm:gap-7" data-testid={`bag-item-${i}`}>
                <Link to={`/piece/${it.slug}`} className="shrink-0 w-24 sm:w-28">
                  <Placeholder variant={it.gradient} aspect="aspect-[3/4]" label="" />
                </Link>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <Link to={`/piece/${it.slug}`} className="font-serif text-lg text-ink hover:text-rose-deep transition-colors" data-testid={`bag-item-name-${i}`}>
                        {it.name[lang]}
                      </Link>
                      <p className="text-[12px] text-ink/50 mt-1">
                        {t.common.size}: {it.size === "Unique" ? (lang === "fr" ? "Taille unique" : lang === "ar" ? "مقاس واحد" : "One size") : it.size}
                        {" · "}
                        {t.common.color}: {COLOR_META[it.color]?.[lang]}
                      </p>
                    </div>
                    <button
                      onClick={() => remove(it.key)}
                      data-testid={`bag-remove-${i}`}
                      aria-label={t.common.remove}
                      className="p-2 text-ink/40 hover:text-rose-deep transition-colors"
                    >
                      <X className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between mt-5">
                    <div className="flex items-center border border-ink/25 rounded-full" data-testid={`bag-qty-stepper-${i}`}>
                      <button onClick={() => setQty(it.key, it.qty - 1)} className="px-3.5 py-2 text-ink/60 hover:text-rose-deep" data-testid={`bag-qty-minus-${i}`} aria-label="-">
                        <Minus className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </button>
                      <span className="w-7 text-center text-sm" data-testid={`bag-qty-value-${i}`}>{it.qty}</span>
                      <button onClick={() => setQty(it.key, it.qty + 1)} className="px-3.5 py-2 text-ink/60 hover:text-rose-deep" data-testid={`bag-qty-plus-${i}`} aria-label="+">
                        <Plus className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </button>
                    </div>
                    <p className="text-[15px] text-ink" data-testid={`bag-line-total-${i}`}>{formatPrice(it.price * it.qty)}</p>
                  </div>
                </div>
              </li>
            ))}
          </ul>

          <div className="lg:sticky lg:top-28 bg-cream border border-ink/10 rounded-sm p-7" data-testid="bag-summary">
            <h2 className="font-serif text-2xl text-ink mb-6">{t.checkout.summaryTitle}</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-ink/60">{t.bag.subtotal}</span>
                <span data-testid="bag-subtotal">{formatPrice(subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink/60">{t.bag.shipping}</span>
                <span data-testid="bag-shipping">{fee === 0 ? t.common.free : formatPrice(fee)}</span>
              </div>
              <div className="flex justify-between pt-3 border-t border-ink/15 text-base font-medium">
                <span>{t.bag.total}</span>
                <span data-testid="bag-total">{formatPrice(total)}</span>
              </div>
            </div>
            {remaining > 0 && (
              <p className="mt-4 text-[12px] text-rose-deep" data-testid="bag-free-note">
                {t.bag.freeNote.replace("{x}", formatPrice(remaining))}
              </p>
            )}
            <Link to="/commande" className="btn btn-ink w-full mt-7" data-testid="bag-checkout-cta">
              {t.bag.checkout}
            </Link>
            <p className="mt-4 text-[11.5px] text-ink/50 text-center leading-relaxed">{t.bag.note}</p>
            <Link to="/boutique" className="block mt-5 text-center text-[12px] uppercase tracking-[0.14em] text-ink/60 hover:text-rose-deep transition-colors" data-testid="bag-continue">
              ← {t.bag.ctaShop}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
